// apps/dashboard/src/lib/seeded-user.ts
//
// Shape shared between the server resolver (lib/server-auth.ts, which imports next/headers)
// and the client provider (context/AuthContext.tsx). It lives in its own module so the
// client never even type-imports from a file that pulls in server-only APIs.

/**
 * The subset of AuthContext's `User` that can be resolved from the session cookie alone.
 *
 * Firebase-derived extras (the provider photo, the id token) are absent — onAuthStateChanged
 * fills those in when it resolves. This is a head start, not a replacement.
 *
 * `deletionScheduledAt` crosses the server/client boundary as an ISO string: a Date would be
 * serialized to one anyway, and being explicit keeps the revive step visible.
 */
export interface SeededUser {
  id: string;
  email: string | null;
  name: string | null;
  photoURL: string | null;
  role: 'STUDENT' | 'ADMIN' | 'INSTRUCTOR';
  onboardingCompleted: boolean;
  accountStatus: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'DELETED';
  deletionScheduledAtISO: string | null;

  // Extended profile fields. GET /v1/users/me already returns the profile and student rows,
  // so carrying them here costs nothing extra and lets the client skip the follow-up
  // getProfile() call it used to make once Firebase resolved (measured on staging as a
  // second /users/me at 1233ms, after the page had already rendered).
  targetExam?: string;
  targetExams?: string[];
  currentGrade?: string;
  educationLevelSlug?: string | null;
  educationLevel?: { slug: string; name: string } | null;
  schoolName?: string;
  audienceSegment?: string | null;
}

/**
 * Maps a GET /v1/users/me body to a SeededUser. Null for anything without an id, so a
 * malformed or error body is never mistaken for a user. Shared by the server resolver and
 * the client's cookie check, so the two cannot drift.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API body, every field read defensively
export function toSeededUser(data: any): SeededUser | null {
  if (!data?.id) return null;
  return {
    id: data.id,
    email: data.email ?? null,
    name: data.profile?.displayName ?? null,
    photoURL: data.profile?.avatarUrl ?? null,
    role: data.role ?? 'STUDENT',
    onboardingCompleted: !!data.onboardingCompleted,
    accountStatus: data.status ?? 'ACTIVE',
    deletionScheduledAtISO: data.deletionScheduledAt
      ? new Date(data.deletionScheduledAt).toISOString()
      : null,
    targetExam: data.profile?.targetExam ?? undefined,
    targetExams: data.profile?.targetExams ?? undefined,
    currentGrade: data.student?.currentGrade ?? undefined,
    educationLevelSlug: data.student?.educationLevelSlug ?? null,
    educationLevel: data.student?.educationLevel ?? null,
    schoolName: data.student?.schoolName ?? undefined,
    audienceSegment: data.profile?.audienceSegment ?? null,
  };
}
